'use client';

import Image from '@/components/Image';
import { EMAIL_INPUT_PATTERN, formatEmail } from '@/components/Form/EmailInput';
import { useJump } from '@/components/MagicJump';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import SubscribeConsentDialog from '@/components/Subscribe/SubscribeConsentDialog';
import { useSubscribeExposureTracking } from '@/components/Subscribe/useSubscribeExposureTracking';
import { toast } from '@/components/Toast';
import { useRouteCnName } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { env } from '@/utils/env';
import { sensorsTrack } from '@/utils/sensors';
import { relationEmailMaxLength } from '@/utils/tools';
import { useTranslations } from '@/i18n/use-translations';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

const Subscribe_US = () => {
  const token = useStore((state) => state.token);
  const storeAccountEmail = useStore((state) => state.userInfo?.email || '');
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const getMarketingStatus = useStore((state) => state.getMarketingStatus);
  const setLoginRegisterShow = useStore((state) => state.setShowLoginRegisterDialog);
  const t = useTranslations('layout.footer');
  const tEmailValidation = useTranslations('components.form.email.validation');
  const toJumpTarget = useJump();
  const pathname = usePathname();
  const pageName = useRouteCnName();
  const [email, setEmail] = useState('');
  const [consentDialogVisible, setConsentDialogVisible] = useState(false);
  const isLoggedIn = Boolean(token);

  useEffect(() => {
    if (storeAccountEmail) {
      setEmail(storeAccountEmail);
    } else if (!token) {
      setEmail('');
    }
  }, [storeAccountEmail, token]);

  useEffect(() => {
    if (!token || storeAccountEmail) return;

    refreshAccountInfo();
  }, [refreshAccountInfo, storeAccountEmail, token]);

  const trackEDMModuleExp = () => {
    sensorsTrack('EDMModule_Exp', {
      page_name: pageName,
    });
  };

  const trackEDMModuleClick = () => {
    sensorsTrack('EDMModule_Click', {
      page_name: pageName,
      btn_name: t('subscribe.subscribeButton'),
    });
  };

  const { ref: exposureRef } = useSubscribeExposureTracking({
    callback: trackEDMModuleExp,
    pathname,
  });

  const handleOnTabSubscribe = async () => {
    if (isLoggedIn) {
      const currentMarketingStatus = await getMarketingStatus();
      if (!currentMarketingStatus?.marketing_granted?.email) {
        setConsentDialogVisible(true);
        return;
      }

      toJumpTarget({
        Jump_target: `/account/my-account`,
        jump_type: 4,
      });
      return;
    }

    const submitEmail = email.trim();
    if (!EMAIL_INPUT_PATTERN.test(submitEmail)) {
      toast.error(tEmailValidation('invalid'));
      return;
    }

    setEmail(submitEmail);
    setLoginRegisterShow({
      show: true,
      initialEmail: submitEmail,
    });
  };

  const handleSubscribeButtonClick = () => {
    trackEDMModuleClick();
    void handleOnTabSubscribe();
  };

  const emailInput = (className: string) => (
    <input
      type="email"
      maxLength={50 + relationEmailMaxLength}
      value={email}
      disabled={isLoggedIn}
      onChange={(event) => setEmail(formatEmail(event.target.value))}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          handleOnTabSubscribe();
        }
      }}
      placeholder={t('subscribe.emailPlaceholder')}
      className={className}
    />
  );

  return (
    <>
      <WapOnly>
        <div ref={exposureRef} className="flex flex-col items-center pt-[30px] pr-10 pb-0 pl-10">
          <b className="inline-block w-[286px] text-center leading-[24px] text-[16px] font-br font-normal text-primary-white">
            Missed a deal? Subscribe to never miss a discount again.
          </b>
          <div className="mt-16 flex items-center">
            {emailInput(
              'w-[222px] h-[40px] bg-primary-white rounded-[4px_0_0_4px] box-border px-10 text-12 text-secondary-text outline-none disabled:cursor-not-allowed disabled:bg-primary-white',
            )}
            <button
              type="button"
              className="h-[40px] leading-[40px] w-[94px] rounded-[0_4px_4px_0] text-center bg-primary-purple text-primary-white font-bb text-14"
              onClick={handleSubscribeButtonClick}
            >
              {t('subscribe.subscribeButton')}
            </button>
          </div>
        </div>
      </WapOnly>
      <WebOnly>
        <div
          ref={exposureRef}
          className={`flex items-center bg-[#282828] text-primary-white px-[60px] py-[86px] ${env.NEXT_PUBLIC_HAS_APP === 'true' ? 'justify-between' : 'justify-center'}`}
        >
          {env.NEXT_PUBLIC_HAS_APP === 'true' ? (
            <div>
              <p className="text-[28px] leading-[34px] font-bb text-primary-white">
                {t('downloadApp.title')}
                <br />
                {t('downloadApp.saveMore')}
              </p>
              <div className="flex">
                <div className="group relative block pt-24 w-[210px] mr-18 cursor-pointer">
                  <Image
                    src="/home-page/ios-app.png"
                    alt={t('downloadApp.appStore')}
                    className="w-[210px]"
                    width={210}
                    height={54}
                  />
                  <Image
                    src="/home-page/app-download-qrcode.png"
                    alt={t('downloadApp.appStore')}
                    className="absolute top-[-270%] left-0 w-full h-auto hidden group-hover:block"
                    width={210}
                    height={210}
                  />
                </div>
                <div className="group relative block pt-24 w-[210px] cursor-pointer">
                  <Image
                    src="/home-page/ios-google.png"
                    alt={t('downloadApp.googlePlay')}
                    className="w-[210px]"
                    width={210}
                    height={54}
                  />
                  <Image
                    className="absolute top-[-270%] left-0 w-full h-auto hidden group-hover:block"
                    src="/home-page/app-download-qrcode.png"
                    alt={t('downloadApp.appStore')}
                    width={210}
                    height={210}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div />
          )}
          <div className="flex flex-col justify-start">
            <div className="text-ellipsis line-clamp-2 break-words whitespace-pre-line w-full font-bb text-28 leading-[34px] mb-18">
              Missed a deal? Subscribe to never miss a discount again.
            </div>
            <div className="flex justify-center w-[54.8vw] h-[64px]">
              {emailInput(
                'flex-1 box-border h-full rounded-[4px_0_0_4px] border border-r-0 border-[#686868] bg-transparent px-14 font-br text-14 text-primary-white outline-none placeholder:text-[#9b9b9b] disabled:cursor-not-allowed disabled:text-[#c8c8c8] disabled:opacity-100',
              )}
              <button
                type="button"
                className="w-[192px] rounded-[0_4px_4px_0] bg-primary-purple text-center font-bb text-16 leading-[64px] text-primary-white cursor-pointer"
                onClick={handleSubscribeButtonClick}
              >
                {t('subscribe.subscribeButton')}
              </button>
            </div>
          </div>
        </div>
      </WebOnly>
      <SubscribeConsentDialog open={consentDialogVisible} onOpenChange={setConsentDialogVisible} />
    </>
  );
};

export default Subscribe_US;
